import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { getDashboardsApi, setDashboardDefaultApi } from "src/api/dashboardApi";
import useDashboardStore from "src/store/dashboardStore";
import type { DashboardListResponse } from "src/types/dashboard";

/**
 * Owns the header's dashboard-list query plus "which dashboard is the
 * default" bookkeeping: picks one on load (the server-flagged one, or just
 * the first dashboard), clears it on unmount, and exposes setDefault for
 * the star toggle in the nav dropdowns.
 */
export function useDefaultDashboard(enabled: boolean) {
  const { defaultDashboard, setDefaultDashboard } = useDashboardStore();
  const queryClient = useQueryClient();

  const { data: allDashboards } = useQuery<DashboardListResponse, Error>({
    queryKey: ["dashboards"],
    queryFn: () => getDashboardsApi(),
    enabled,
    staleTime: 0,
  });

  useEffect(() => {
    const foundDashboard = allDashboards?.dashboards?.find(
      (dash) => dash.is_default === true,
    );

    if (!defaultDashboard)
      if (foundDashboard) setDefaultDashboard(foundDashboard.code);
      else setDefaultDashboard(allDashboards?.dashboards?.[0]?.code ?? "");

    return () => {
      if (defaultDashboard) setDefaultDashboard("");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDashboards, defaultDashboard]);

  const { mutate: setDefault } = useMutation({
    mutationFn: (code: string) => setDashboardDefaultApi(code),
    onSuccess: (_data, code) => {
      setDefaultDashboard(code);
      queryClient.invalidateQueries({ queryKey: ["dashboards"] });
      toast.success("Default dashboard updated.");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to set default dashboard.");
    },
  });

  return { allDashboards, setDefault };
}
